import React, { useState, useCallback } from 'react';
import { ArrowLeft, Shield, UserCircle, X, AlertCircle, Loader, Info } from 'lucide-react';
import PopUpModel from '../../components/PopUpModel';
import { useNavigate } from 'react-router-dom';
import { nanoid } from 'nanoid';
import RestApi from '../../Apis/RestApi';
import { toast } from 'react-toastify';
import QRCodeStyling from 'qr-code-styling';

// Constants
const CHUNK_SIZE = 50; // Reduced chunk size for better memory management
const MAX_QR_CODES = 1000000; // 1 million QR codes limit
const DEFAULT_QR_SIZE = 256;
const BATCH_PROCESSING_DELAY = 100; 

const QRCreationForm = () => {
  // Form Data State
  const [formData, setFormData] = useState({
    inputLink: '',
    numberOfQR: '1',
    name: '',
    size: '256',
    singleUse: false,
    batches: false,
    numberOfBatches: '',
    itemsPerBatch: '',
  });

  // Member Management States
  const [memberInvite, setMemberInvite] = useState({
    email: '',
    role: 'viewer'
  });
  
  // UI States
  const [invitedMembers, setInvitedMembers] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  // Processing States
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentOperation, setCurrentOperation] = useState('');
  const navigate = useNavigate();
  
  // Processing Statistics
  const [processingStats, setProcessingStats] = useState({
    totalProcessed: 0,
    currentBatch: 0,
    totalBatches: 0,
    errors: [],
    startTime: null,
    estimatedTimeRemaining: null
  });

  // QR Image Generation
  const generateQRImage = useCallback(async (shortId, size = DEFAULT_QR_SIZE) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
  
    try {
      const qrCode = new QRCodeStyling({
        width: size,
        height: size,
        data: `https://s-qc.in/redirect/${shortId}`,
        margin: 10,
        dotsOptions: { color: "#000000", type: "square" },
        backgroundOptions: { color: "#ffffff" }
      });
  
      await qrCode.append(container);
      
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          try {
            const canvas = container.querySelector('canvas');
            if (!canvas) throw new Error('Canvas not found');
            
            const base64Image = canvas.toDataURL('image/png');
            resolve(base64Image);
          } catch (err) {
            reject(err);
          } finally {
            document.body.removeChild(container);
          }
        }, 50);
      });
    } catch (error) {
      document.body.removeChild(container);
      throw error;
    }
  }, []);
  
  // Batch Processing Function
  const processQRBatch = async (start, end, batchNum = null) => {
    const qrCodes = [];
    const userId = localStorage.getItem('_id');
    const errors = [];
  
    for (let i = start; i < end; i++) {
      try {
        const shortId = nanoid(8);
        const qrImage = await generateQRImage(shortId, parseInt(formData.size));
  
        qrCodes.push({
          user_id: userId,
          qr_short_id: shortId,
          qr_url: formData.inputLink,
          qr_type: formData.singleUse ? 'single' : 'dynamic',
          qr_image: qrImage,
          qr_name: formData.batches && batchNum !== null
            ? `${formData.name || 'QR'}_B${batchNum}_${i - start + 1}`
            : `${formData.name || 'QR'}_${i + 1}`,
          settings: {
            isActive: true,
            maxScans: formData.singleUse ? 1 : null,
            ...(formData.batches && batchNum !== null && {
              batch_info: {
                batch_number: batchNum,
                item_number: i - start + 1,
                total_batches: parseInt(formData.numberOfBatches),
                items_per_batch: parseInt(formData.itemsPerBatch)
              }
            })
          }
        });
  
        // Memory management - pause briefly every 10 QR codes
        if (qrCodes.length % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
  
        // Update progress
        setProgress(((i - start + 1) / (end - start)) * 100);
        setProcessingStats(prev => ({
          ...prev,
          totalProcessed: prev.totalProcessed + 1
        }));
  
      } catch (error) {
        errors.push(`Error at index ${i}: ${error.message}`);
        console.error(`Error generating QR code ${i + 1}:`, error);
      }
    }
  
    return { qrCodes, errors };
  };
  
  // Input Validation Function
  const validateInput = () => {
    const newErrors = {};
    
    if (!formData.inputLink.trim()) {
      newErrors.inputLink = "Please enter a valid URL";
    }
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    const totalQRs = formData.batches 
      ? parseInt(formData.numberOfBatches) * parseInt(formData.itemsPerBatch)
      : parseInt(formData.numberOfQR);
  
    if (isNaN(totalQRs) || totalQRs <= 0) {
      newErrors.numberOfQR = "Please enter valid numbers for QR code generation";
    }
  
    if (totalQRs > MAX_QR_CODES) {
      newErrors.numberOfQR = `Maximum limit of ${MAX_QR_CODES.toLocaleString()} QR codes exceeded`;
    }
  
    if (formData.batches) {
      if (!formData.numberOfBatches || parseInt(formData.numberOfBatches) < 1) {
        newErrors.numberOfBatches = "Number of batches must be at least 1";
      }
      
      if (!formData.itemsPerBatch || parseInt(formData.itemsPerBatch) < 1) {
        newErrors.itemsPerBatch = "Items per batch must be at least 1";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Time Calculation Helper
  const calculateTimeRemaining = (processed, total, startTime) => {
    if (processed === 0) return null;
    
    const elapsed = Date.now() - startTime;
    const rate = processed / elapsed;
    const remaining = (total - processed) / rate;
    
    return Math.ceil(remaining / 1000); // Convert to seconds
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateInput()) {
      return;
    }
  
    setLoading(true);
    setProgress(0);
    setCurrentOperation('Processing bulk QR code request...');
  
    try {
      const totalQRs = formData.batches 
        ? parseInt(formData.numberOfBatches) * parseInt(formData.itemsPerBatch)
        : parseInt(formData.numberOfQR);
  
      // Prepare QR metadata
      const qrMetadata = {
        user_id: localStorage.getItem('_id'),
        base_url: formData.inputLink,
        qr_type: formData.singleUse ? 'single' : 'dynamic',
        size: parseInt(formData.size),
        base_name: formData.name || 'QR',
        total_qrs: totalQRs,
        settings: {
          isActive: true,
          maxScans: formData.singleUse ? 1 : null
        }
      };
  
      // Add batch information if enabled
      if (formData.batches) {
        qrMetadata.batch_info = {
          total_batches: parseInt(formData.numberOfBatches),
          items_per_batch: parseInt(formData.itemsPerBatch)
        };
      }
  
      setProcessingStats({
        totalProcessed: 0,
        currentBatch: 0,
        totalBatches: formData.batches ? parseInt(formData.numberOfBatches) : 1,
        errors: [],
        startTime: Date.now(),
        estimatedTimeRemaining: null
      });
  
      setCurrentOperation('Creating QR codes...');
      const response = await RestApi.createMultipleQRs({ qrMetadata });
      
      if (response.data.status) {
        toast.success(`Successfully initiated creation of ${totalQRs} QR codes`);
        navigate('/home/enterprise');
      } else {
        throw new Error(response.data.msg || 'Failed to create QR codes');
      }
  
    } catch (error) {
      console.error("Error creating bulk QR codes:", error);
      toast.error(error.message || "Failed to create QR codes");
      setProcessingStats(prev => ({
        ...prev,
        errors: [...prev.errors, error.message]
      }));
    } finally {
      setLoading(false);
      setCurrentOperation('');
    }
  };
  
  // Form input handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // Reset batch fields when disabling batch processing
      ...(name === 'batches' && !checked ? {
        numberOfBatches: '',
        itemsPerBatch: ''
      } : {})
    }));

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // Member management handlers
  const handleMemberInviteChange = (e) => {
    const { name, value } = e.target;
    setMemberInvite(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleInviteMember = (e) => {
    e.preventDefault();
    if (!memberInvite.email) {
      toast.error("Please enter an email address");
      return;
    }
  
    setInvitedMembers(prev => [
      ...prev, 
      { 
        ...memberInvite, 
        status: 'pending',
        id: nanoid(6)  // Add unique ID for each member
      }
    ]);
    
    setMemberInvite({ email: '', role: 'viewer' });
    setShowInviteForm(false);
    toast.success("Member invited successfully");
  };
  
  const handleRemoveMember = (emailToRemove) => {
    setInvitedMembers(prev => 
      prev.filter(member => member.email !== emailToRemove)
    );
    toast.info("Member removed");
  };
  
  // Navigation handler
  const handleBack = () => {
    if (loading) {
      const confirmExit = window.confirm('QR code generation is in progress. Are you sure you want to leave?');
      if (!confirmExit) return;
    }
    navigate(-1);
  };

  const calculateTotalQRs = () => {
    if (formData.batches && formData.numberOfBatches && formData.itemsPerBatch) {
      return parseInt(formData.numberOfBatches) * parseInt(formData.itemsPerBatch);
    }
    return parseInt(formData.numberOfQR) || 0;
  };

  const calculateEstimate = () => {
    const totalQRs = calculateTotalQRs();
    const pricePerQR = 0.01; // $0.01 per QR code
    return `$${(totalQRs * pricePerQR).toFixed(2)}`;
  };
  
  // Role icon helper
  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4 text-red-500" />;
      case 'editor':
        return <UserCircle className="w-4 h-4 text-blue-500" />;
      default:
        return <UserCircle className="w-4 h-4 text-gray-500" />;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0f172a]">
      {/* Navigation Bar */}
      <div className="bg-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-xl font-semibold text-gray-800">Manage QR Codes</h1>
        </div>
      </div>

      {/* Form Container */}
      <div className="flex items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-xl p-8 h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">QR Code Generator</h2>
          
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.submit}
            </div>
          )}
          
          {/* Progress Display */}
          {loading && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex items-center gap-2 text-blue-700 mb-2">
                {processingStats.errors.length > 0 ? (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                ) : (
                  <Loader className="h-5 w-5 animate-spin" />
                )}
                <span>{currentOperation}</span>
              </div>
              
              <div className="space-y-2">
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
        
                {/* Progress Stats */}
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Processed: {processingStats.totalProcessed} QR codes</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  
                  {formData.batches && (
                    <div>
                      Batch: {processingStats.currentBatch} of {processingStats.totalBatches}
                    </div>
                  )}
                  
                  {processingStats.estimatedTimeRemaining && (
                    <div>
                      Estimated time remaining: {processingStats.estimatedTimeRemaining}s
                    </div>
                  )}
                </div>
        
                {/* Error Display */}
                {processingStats.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-red-600 font-medium">Errors encountered:</div>
                    <div className="max-h-24 overflow-y-auto">
                      {processingStats.errors.map((error, index) => (
                        <div key={index} className="text-sm text-red-500 bg-red-50 p-1 rounded">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Input Link */}
            <div>
              <label htmlFor="inputLink" className="block text-sm font-medium text-gray-700 mb-1">Destination URL</label>
              <input
                type="text"
                id="inputLink"
                name="inputLink"
                value={formData.inputLink}
                onChange={handleInputChange}
                placeholder="Enter the URL you want to redirect to"
                className={`w-full p-3 border-2 ${errors.inputLink ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
              />
              {errors.inputLink && <p className="mt-1 text-red-500 text-sm">{errors.inputLink}</p>}
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Base Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter a base name for your QR codes"
                className={`w-full p-3 border-2 ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
              />
              {errors.name && <p className="mt-1 text-red-500 text-sm">{errors.name}</p>}
            </div>

            {/* Number of QR Codes */}
            <div>
              <label htmlFor="numberOfQR" className="block text-sm font-medium text-gray-700 mb-1">Number of QR Codes to Create</label>
              <input
                type="number"
                id="numberOfQR"
                name="numberOfQR"
                value={formData.numberOfQR}
                onChange={handleInputChange}
                min="1"
                max={MAX_QR_CODES}
                disabled={formData.batches}
                className={`w-full p-3 border-2 ${errors.numberOfQR ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500 ${formData.batches ? 'bg-gray-100' : ''}`}
              />
              {errors.numberOfQR && <p className="mt-1 text-red-500 text-sm">{errors.numberOfQR}</p>}
              {formData.batches && <p className="mt-1 text-gray-500 text-sm">Total QR codes will be calculated from batch settings</p>}
            </div>

            {/* QR Size */}
            <div>
              <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1">QR Code Size</label>
              <select
                id="size"
                name="size"
                value={formData.size}
                onChange={handleInputChange}
                className="w-full p-3 border-2 border-gray-300 rounded text-black focus:outline-none focus:border-blue-500"
              >
                <option value="256">256x256 (standard)</option>
                <option value="512">512x512 (large)</option>
                <option value="740">740x740 (extra large)</option>
              </select>
            </div>

            {/* Single Use Button */}
            <div className="flex items-center gap-2 p-3 border-2 border-gray-300 rounded">
              <input
                type="checkbox"
                id="singleUse"
                name="singleUse"
                checked={formData.singleUse}
                onChange={handleInputChange}
                className="w-5 h-5 rounded border-gray-300"
              />
              <label htmlFor="singleUse" className="text-gray-700">Single Use QR Codes</label>
              <div className="ml-auto flex items-center text-gray-500 text-sm">
                <Info className="w-4 h-4 mr-1" />
                <span>QR codes will expire after one scan</span>
              </div>
            </div>

            {/* Batches Checkbox and Conditional Fields */}
            <div className="p-4 border-2 border-gray-300 rounded">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="batches"
                  name="batches"
                  checked={formData.batches}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <label htmlFor="batches" className="text-gray-700 font-medium">Enable Batch Processing</label>
              </div>
              
              <p className="mt-1 mb-3 text-gray-500 text-sm">Create QR codes in organized batches with sequential naming</p>

              {formData.batches && (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="numberOfBatches" className="block text-sm font-medium text-gray-700 mb-1">Number of Batches</label>
                      <input
                        type="number"
                        id="numberOfBatches"
                        name="numberOfBatches"
                        value={formData.numberOfBatches}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="e.g., 5"
                        className={`w-full p-3 border-2 ${errors.numberOfBatches ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
                      />
                      {errors.numberOfBatches && <p className="mt-1 text-red-500 text-sm">{errors.numberOfBatches}</p>}
                    </div>
                    <div>
                      <label htmlFor="itemsPerBatch" className="block text-sm font-medium text-gray-700 mb-1">Items per Batch</label>
                      <input
                        type="number"
                        id="itemsPerBatch"
                        name="itemsPerBatch"
                        value={formData.itemsPerBatch}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="e.g., 10"
                        className={`w-full p-3 border-2 ${errors.itemsPerBatch ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
                      />
                      {errors.itemsPerBatch && <p className="mt-1 text-red-500 text-sm">{errors.itemsPerBatch}</p>}
                    </div>
                  </div>
                  
                  {formData.numberOfBatches && formData.itemsPerBatch && (
                    <div className="p-3 bg-blue-50 rounded border border-blue-200 text-blue-800">
                      <p className="font-medium">Total QR codes to create: {calculateTotalQRs()}</p>
                      <p className="text-sm">Example naming: {formData.name || 'QR'}_B1_1, {formData.name || 'QR'}_B1_2, etc.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Member Management Section */}
            <div className="border-2 border-gray-300 rounded p-4">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Member Management</h3>
              
              {/* Invited Members List */}
              {invitedMembers.length > 0 && (
                <div className="mb-4 space-y-2">
                  {invitedMembers.map((member, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3">
                        {getRoleIcon(member.role)}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{member.email}</div>
                          <div className="text-xs text-gray-500 capitalize">{member.role}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
                          {member.status}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveMember(member.email)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Invite Form or Button */}
              {!showInviteForm ? (
                <button
                  type="button"
                  onClick={() => setShowInviteForm(true)}
                  className="bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition-colors border border-gray-300"
                >
                  + Invite Member
                </button>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Invite Member</h3>
                    <button
                      type="button"
                      onClick={() => setShowInviteForm(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <input
                      type="email"
                      name="email"
                      value={memberInvite.email}
                      onChange={handleMemberInviteChange}
                      placeholder="Enter email address"
                      className="w-full p-3 border-2 border-gray-300 rounded text-black focus:outline-none focus:border-blue-500"
                    />
                    <select
                      name="role"
                      value={memberInvite.role}
                      onChange={handleMemberInviteChange}
                      className="w-full p-3 border-2 border-gray-300 rounded text-black focus:outline-none focus:border-blue-500"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      type="button"
                      onClick={handleInviteMember}
                      className="w-full bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
                    >
                      Send Invite
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Section */}
            <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
              <div className="border-2 border-gray-300 rounded px-4 py-2 text-gray-700 bg-gray-50">
                <span className="text-sm font-medium">Estimated Cost: </span>
                <span className="font-bold">{calculateEstimate()}</span>
              </div>

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="bg-gray-100 text-gray-800 px-6 py-2 rounded hover:bg-gray-200 transition-colors border border-gray-300"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-blue-600 text-white px-8 py-2 rounded hover:bg-blue-700 transition-colors ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? 'Creating QR Codes...' : 'Generate QR Codes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* Modal */}
      <PopUpModel 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default QRCreationForm;